import type { ErrorResponse } from '@fuel-system/contracts';

/**
 * Rule E8: a request whose deadline elapsed before any response arrived. The
 * request may already have completed on the server - the client only knows it
 * stopped waiting. Deliberately not an `ApiRequestError`: no server payload
 * was ever received, so there is no status, code or requestId to carry, and
 * `useApiError()` must not treat this as a confirmed failure (rule E17).
 */
/**
 * The synthetic id `api-client` falls back to when a response carries no
 * `x-request-id`. Rule E15: a correlation reference is shown only when the
 * server actually issued one — a fabricated id sends a user to support with
 * something nobody can look up.
 */
export const UNKNOWN_REQUEST_ID = 'unknown';

export class ApiTimeoutError extends Error {
  constructor() {
    super('Request timed out; the outcome is unknown.');
    this.name = 'ApiTimeoutError';
  }
}

/** A typed non-success response returned by the API client. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly payload: ErrorResponse;

  constructor(status: number, payload: ErrorResponse) {
    super(payload.error.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.payload = payload;
  }

  get code(): ErrorResponse['error']['code'] {
    return this.payload.error.code;
  }

  get requestId(): string {
    return this.payload.requestId;
  }

  get fieldErrors(): NonNullable<ErrorResponse['error']['fieldErrors']> {
    return this.payload.error.fieldErrors ?? [];
  }

  /** Interpolation values for the translated message, e.g. `{ limit: "1000.00000" }`. */
  get params(): NonNullable<ErrorResponse['error']['params']> {
    return this.payload.error.params ?? {};
  }
}
